import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EmailVerificationBanner = ({ user, onVerified }) => {
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  if (user?.email_verified) return null;

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, null, {
        params: { code }
      });
      toast.success('Email verified successfully!');
      if (onVerified) onVerified();
      setShowCodeInput(false);
      setCode('');
    } catch (error) {
      console.error('Verification failed:', error);
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`);
      toast.success('Verification code sent to your email');
      setShowCodeInput(true);
    } catch (error) {
      console.error('Resend failed:', error);
      toast.error('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-accent-yellow/10 border-accent-yellow/30 mb-6" data-testid="email-verification-banner">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">📧</div>
          <div className="flex-1 space-y-3">
            <div>
              <h4 className="font-semibold text-sm mb-1">Verify your email</h4>
              <p className="text-sm text-muted-foreground">
                Please verify your email to create services and send messages.
              </p>
            </div>

            {showCodeInput ? (
              <div className="flex gap-2">
                <Input
                  placeholder="Enter 6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="max-w-xs"
                  data-testid="verification-code-input"
                />
                <Button
                  onClick={handleVerify}
                  disabled={loading || code.length !== 6}
                  size="sm"
                  className="rounded-xl"
                  data-testid="verify-button"
                >
                  Verify
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleResend}
                disabled={loading}
                size="sm"
                variant="outline"
                className="rounded-xl"
                data-testid="resend-verification-button"
              >
                {loading ? 'Sending...' : 'Resend Verification Code'}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
